<template>
  <div class="bg-white rounded-lg border border-gray-200 p-5">
    <h3 class="text-lg font-semibold text-gray-900">{{ concept.title }}</h3>
    <p class="mt-2 text-gray-600 text-sm leading-relaxed">{{ concept.explanation }}</p>
    <div v-if="examples.length" class="mt-3">
      <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Examples</h4>
      <ul class="space-y-1.5">
        <li
          v-for="(example, i) in examples"
          :key="i"
          class="flex items-start gap-2 text-sm bg-emerald-50 text-emerald-800 px-3 py-2 rounded"
        >
          <!-- The API says which examples actually have a clip. Building the URL
               here meant every example got a play button, including unit 0's
               English descriptions of the writing system, which have no audio
               on purpose — those buttons 404'd. -->
          <AudioButton
            v-if="concept.example_audio_urls?.[i]"
            :src="concept.example_audio_urls[i]"
            :show-slow="true"
            class="shrink-0"
          />
          <div class="min-w-0 flex-1">
            <p class="font-medium">
              <TappableText :tokens="tokens[i]" :text="example.vn" :source="source" />
            </p>
            <!-- Literal gloss sits between the Vietnamese and the natural
                 English, which is where it does its work: it shows how the
                 sentence is actually built before saying what it means. -->
            <p v-if="example.literal" class="mt-0.5 text-xs text-emerald-600/70 italic">
              {{ example.literal }}
            </p>
            <p v-if="example.en" class="mt-0.5 text-xs text-emerald-700/80">{{ example.en }}</p>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import AudioButton from './shared/AudioButton.vue'
import TappableText from './shared/TappableText.vue'
import { annotateTexts } from '../utils/lookup'

const props = defineProps({
  concept: { type: Object, required: true },
})

// Examples are authored as "Ngon quá trời! (Insanely delicious!)" — Vietnamese
// first, natural English in trailing parens. Same split the audio rake task
// uses, so the clip and the rendered Vietnamese always agree. Object-shaped
// examples ({ vn, en, literal }) pass straight through.
function parseExample(raw) {
  if (raw && typeof raw === 'object') {
    return { vn: raw.vn ?? '', en: raw.en ?? '', literal: raw.literal ?? '' }
  }

  const text = String(raw ?? '')
  const open = text.indexOf('(')
  if (open === -1) return { vn: text.trim(), en: '', literal: '' }

  return {
    vn: text.slice(0, open).trim(),
    en: text.slice(open + 1).replace(/\)\s*$/, '').trim(),
    literal: '',
  }
}

// literal_glosses is a positional array parallel to examples (see the
// gloss:grammar rake task), and may be absent or partially filled.
const examples = computed(() => {
  const glosses = props.concept.literal_glosses ?? []
  return (props.concept.examples ?? []).map((raw, i) => {
    const parsed = parseExample(raw)
    return { ...parsed, literal: parsed.literal || glosses[i] || '' }
  })
})
const source = computed(() => ({ kind: 'grammar', id: props.concept.id }))
const tokens = ref([])

watch(
  examples,
  async (list) => {
    tokens.value = []
    if (!list.length) return
    tokens.value = await annotateTexts(list.map(e => e.vn))
  },
  { immediate: true }
)
</script>
